/* Humbler search: Spotlight-style quick search (snippets/humbler-search.liquid, assets/humbler-search.css).
   Opens from [data-hb-search-open], and from ⌘K / Ctrl+K or "/" when the shopper is not typing in a field;
   ⌘K / Ctrl+K and Esc close it.
   Native: Shopify predictive search, GET {root}search/suggest.json for products, collections, pages and
   query suggestions. Product prices there are decimal strings in the shopper's currency; they become cents
   and go through HumblerUI.money, like every other Humbler price. Preview (html[data-humbler-preview]):
   the same result shape is built from /preview-data.json (HumblerUI.loadPreviewData).
   Speed: 120ms debounce, one in-flight request (AbortController), results cached per query.
   Keyboard: ↑/↓ move the active option (aria-activedescendant on the combobox input), Enter opens it, or
   submits to /search when none is active.
   Local storage, only while Shopify's customer privacy allows preference storage:
     'humbler:search:recent:v1'  recent search terms (max 6)
     'humbler:search:viewed:v1'  recently viewed products { url, title, image, finish } (max 8, written on product pages)
   Product rows read like the cards: titles cleaned as snippets/humbler-product-title does (no finish suffix, never
   all caps) and each row names its finish (snippets/humbler-finish), so gold and silver twins stay apart.
   CPB order records (type cpb_hybrid, handle cpb-order-*: snippets/humbler-personalised 'hidden') are never listed:
   the suggest section leaves them out, and so do the JSON fallback and the preview here. */
(() => {
  if (window.HumblerSearch) return;

  const RECENT_KEY = 'humbler:search:recent:v1';
  const VIEWED_KEY = 'humbler:search:viewed:v1';
  const EASE_OUT = 'cubic-bezier(.16, 1, .3, 1)';
  const LIMITS = { products: 7, collections: 3, pages: 3, queries: 4, recent: 4, viewed: 4 };
  const FINISHES = ['gold', 'silver', 'black'];
  const SUGGEST_SECTION = 'humbler-search-suggest';
  const wide = window.matchMedia('(min-width: 700px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // A CPB order record (snippets/humbler-personalised 'hidden'): its description can hold a customer's engraving.
  const hiddenProduct = product => String(product?.type || product?.product_type || '') === 'cpb_hybrid' ||
    /^cpb-order-/.test(String(product?.handle || '')) || /\/products\/cpb-order-/.test(String(product?.url || ''));

  const ICONS = {
    search: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><circle cx="10.5" cy="10.5" r="6.75" stroke="currentColor" stroke-width="1.5"/><path d="m15.5 15.5 5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    clock: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.25" stroke="currentColor" stroke-width="1.5"/><path d="M12 7.75V12l2.75 1.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    page: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M6.25 3.75h7.5l4 4v12.5H6.25z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M13.5 3.75V8h4.25M9 12.25h6M9 15.75h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    grid: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><rect x="4.25" y="4.25" width="6.5" height="6.5" rx="1" stroke="currentColor" stroke-width="1.5"/><rect x="13.25" y="4.25" width="6.5" height="6.5" rx="1" stroke="currentColor" stroke-width="1.5"/><rect x="4.25" y="13.25" width="6.5" height="6.5" rx="1" stroke="currentColor" stroke-width="1.5"/><rect x="13.25" y="13.25" width="6.5" height="6.5" rx="1" stroke="currentColor" stroke-width="1.5"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M4 12h15.5m-6-6.5L20 12l-6.5 6.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    enter: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M19.25 5.5v6.25a2.5 2.5 0 0 1-2.5 2.5H5.5m0 0L9.5 10m-4 4.25 4 4.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  const readJson = node => {
    try { return JSON.parse(node?.textContent || 'null'); } catch { return null; }
  };
  const fill = (text, values) => String(text ?? '').replace(/%(\w+)%/g, (match, name) => (name in values ? values[name] : match));
  const normalize = value => String(value ?? '').trim().replace(/\s+/g, ' ');
  const pathOf = url => {
    try { return new URL(url, location.href).pathname.replace(/\/$/, ''); } catch { return String(url || ''); }
  };

  /* ---------- storage (preferences) ---------- */
  function storageAllowed() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.preferencesProcessingAllowed === 'function') return privacy.preferencesProcessingAllowed() !== false;
    } catch { /* API unavailable: no consent requirement known */ }
    return true;
  }
  const store = {
    read(key) {
      if (!storageAllowed()) return [];
      try {
        const value = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(value) ? value : [];
      } catch { return []; }
    },
    write(key, list) {
      if (!storageAllowed()) return;
      try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* storage full or blocked */ }
    },
    clear(key) {
      try { localStorage.removeItem(key); } catch { /* storage blocked */ }
    }
  };
  // A CPB order record's page (reached from its bag line) is never remembered, and one stored before is never shown.
  function rememberViewed(item) {
    if (!item || !item.url || !item.title || hiddenProduct(item)) return;
    const path = pathOf(item.url);
    const entry = { url: path, title: String(item.title).slice(0, 140), image: item.image || null, finish: FINISHES.includes(item.finish) ? item.finish : '' };
    store.write(VIEWED_KEY, [entry, ...store.read(VIEWED_KEY).filter(other => other && other.url !== path)].slice(0, 8));
  }

  /* ---------- text helpers ---------- */
  // Display title, as snippets/humbler-product-title prints it on the PDP: no trailing finish label
  // ("… - Gold", "… (Silver)"), all-caps catalog titles in Title Case, size units as "3mm".
  const FINISH_SUFFIX = /\s+(?:[-–—|]\s*|\()(?:rose gold|gold|silver|black|platinum)\)?$/i;
  function displayTitle(title) {
    const raw = String(title ?? '').trim();
    let text = raw.replace(FINISH_SUFFIX, '').trim() || raw;
    if (/[A-Z]/.test(text) && text === text.toUpperCase()) {
      text = text.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    return text.replace(/(\d)mm\b/gi, '$1mm');
  }
  function escape(value) {
    return window.HumblerUI ? window.HumblerUI.escape(value) : String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  }
  // Wraps word-start matches of the query terms in <mark>; everything else is escaped.
  function highlight(text, query) {
    const source = String(text ?? '');
    const lower = source.toLowerCase();
    const terms = [...new Set(normalize(query).toLowerCase().split(' ').filter(Boolean))].sort((a, b) => b.length - a.length);
    if (!terms.length || lower.length !== source.length) return escape(source);
    const ranges = [];
    for (const term of terms) {
      let index = lower.indexOf(term);
      while (index !== -1) {
        if (index === 0 || !/[\p{L}\p{N}]/u.test(lower[index - 1])) ranges.push([index, index + term.length]);
        index = lower.indexOf(term, index + 1);
      }
    }
    if (!ranges.length) return escape(source);
    ranges.sort((a, b) => a[0] - b[0]);
    const merged = [ranges[0].slice()];
    for (const [from, to] of ranges.slice(1)) {
      const last = merged[merged.length - 1];
      if (from <= last[1]) last[1] = Math.max(last[1], to);
      else merged.push([from, to]);
    }
    let html = '';
    let position = 0;
    for (const [from, to] of merged) {
      html += `${escape(source.slice(position, from))}<mark>${escape(source.slice(from, to))}</mark>`;
      position = to;
    }
    return html + escape(source.slice(position));
  }

  /* ---------- data ---------- */
  // Shopify predictive search through sections/humbler-search-suggest (Section Rendering API): the same
  // results as search/suggest.json plus each product's finish. If that section cannot be read, the JSON
  // endpoint answers instead (rows then show no finish).
  let suggestSection = true;
  async function nativeSearch(query, signal) {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,page,query',
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last'
    });
    if (suggestSection) {
      try {
        const response = await fetch(`${root}search/suggest?${params}&section_id=${SUGGEST_SECTION}`, { signal });
        if (!response.ok) throw new Error(`search ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = readJson(doc.querySelector('[data-hb-search-suggest]'));
        if (!data || !Array.isArray(data.products)) throw new Error('search section');
        return {
          products: data.products.map(product => ({
            title: product.title,
            url: product.url,
            image: product.image || null,
            price: Math.round(Number(product.price) || 0),
            compare: Math.round(Number(product.compare) || 0),
            available: product.available !== false,
            finish: FINISHES.includes(product.finish) ? product.finish : ''
          })),
          collections: Array.isArray(data.collections) ? data.collections : [],
          pages: Array.isArray(data.pages) ? data.pages : [],
          queries: Array.isArray(data.queries) ? data.queries : []
        };
      } catch (error) {
        if (error && error.name === 'AbortError') throw error;
        suggestSection = false; // e.g. the section is not on this theme: use the JSON endpoint from now on
      }
    }
    const response = await fetch(`${root}search/suggest.json?${params}`, { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`search ${response.status}`);
    const json = await response.json();
    const results = (json.resources && json.resources.results) || {};
    return {
      products: (results.products || []).filter(product => !hiddenProduct(product)).map(product => ({
        title: product.title,
        url: product.url,
        image: product.featured_image?.url || product.image || null,
        price: Math.round(Number(product.price) * 100),
        compare: Math.round(Number(product.compare_at_price_max || 0) * 100),
        available: product.available !== false,
        finish: ''
      })),
      collections: (results.collections || []).map(collection => ({ title: collection.title, url: collection.url, image: collection.featured_image?.url || null })),
      pages: (results.pages || []).map(page => ({ title: page.title, url: page.url })),
      queries: (results.queries || []).map(item => ({ text: item.text }))
    };
  }

  // Preview: snippets/humbler-finish's order on the catalog's data: none for a piece sold in several metals (an
  // option named "Pendant", or one naming two or more of gold, silver and black), a single-value
  // finish/color/metal option, "black" in the handle or title, the store's gold/silver collections, then
  // "gold"/"silver" in the handle.
  function previewFinish(product) {
    const metalOf = value => FINISHES.find(word => String(value || '').toLowerCase().includes(word)) || '';
    const several = (product.options || []).some((name, index) => {
      const values = new Set((product.variants || []).map(variant => (variant.options || [])[index]));
      return values.size > 1 && (/^pendant$/i.test(String(name).trim()) || new Set([...values].map(metalOf).filter(Boolean)).size > 1);
    });
    if (several) return '';
    let finish = '';
    (product.options || []).forEach((name, index) => {
      if (!/^(finish|colou?r|metal|pendant)$/i.test(String(name).trim())) return;
      const values = new Set((product.variants || []).map(variant => (variant.options || [])[index]));
      if (values.size === 1) finish = String([...values][0] || '').toLowerCase();
    });
    const words = String(product.handle || '').split('-');
    const title = ` ${String(product.title || '').toLowerCase()} `;
    if (!finish && (words.includes('black') || title.includes('(black)') || title.includes(' black '))) finish = 'black';
    if (!finish) finish = (product.collections || []).map(handle => String(handle).split('-')[0]).find(prefix => prefix === 'gold' || prefix === 'silver') || '';
    if (!finish) finish = words.includes('gold') ? 'gold' : words.includes('silver') ? 'silver' : '';
    if (finish.includes('gold')) finish = 'gold';
    else if (finish.includes('silver')) finish = 'silver';
    return FINISHES.includes(finish) ? finish : '';
  }

  // Preview: the same shape from the cached public catalog (no request leaves the machine).
  async function previewSearch(query) {
    const data = await window.HumblerUI.loadPreviewData();
    const needle = query.toLowerCase();
    const terms = needle.split(' ').filter(Boolean);
    const matches = text => terms.every(term => text.includes(term));
    const startsWord = (text, term) => text.startsWith(term) || text.includes(` ${term}`);
    const rank = title => {
      const text = title.toLowerCase();
      return text.startsWith(needle) ? 0 : startsWord(text, terms[0]) ? 1 : 2;
    };
    const products = (data.products || [])
      .filter(product => !hiddenProduct(product))
      .filter(product => matches(`${product.title} ${product.handle} ${(product.tags || []).join(' ')} ${product.type || ''}`.toLowerCase()))
      .sort((a, b) => (a.available === false) - (b.available === false) || rank(a.title) - rank(b.title))
      .slice(0, 8)
      .map(product => ({
        title: product.title,
        url: product.url,
        image: product.image,
        price: Math.min(...product.variants.map(variant => variant.price)),
        compare: Math.max(0, ...product.variants.map(variant => variant.compare_at_price || 0)),
        available: product.available !== false,
        finish: previewFinish(product)
      }));
    const collections = (data.collections || [])
      .filter(collection => matches(`${collection.title} ${collection.handle}`.toLowerCase()))
      .sort((a, b) => rank(a.title) - rank(b.title))
      .slice(0, 8)
      .map(collection => ({ title: collection.title, url: collection.url, image: collection.image || null }));
    const pages = (data.pages || [])
      .filter(page => matches(page.title.toLowerCase()))
      .slice(0, 8)
      .map(page => ({ title: page.title, url: page.url }));
    // Query suggestions: catalog phrases that start at a word matching the last term.
    const last = terms[terms.length - 1];
    const phrases = new Set();
    for (const title of [...(data.collections || []).map(item => item.title), ...(data.products || []).filter(item => !hiddenProduct(item)).map(item => item.title)]) {
      const words = title.toLowerCase().replace(/[^\p{L}\p{N}\s'-]/gu, ' ').split(/\s+/).filter(Boolean);
      const at = words.findIndex(word => word.startsWith(last));
      if (at === -1) continue;
      const phrase = [...terms.slice(0, -1), ...words.slice(at, at + 2)].join(' ');
      if (phrase !== needle) phrases.add(phrase);
      if (phrases.size >= 8) break;
    }
    return { products, collections, pages, queries: [...phrases].map(text => ({ text })) };
  }

  /* ---------- the dialog ---------- */
  class Search {
    constructor(dialog) {
      this.dialog = dialog;
      this.ui = window.HumblerUI;
      this.preview = !!this.ui.isPreview;
      this.panel = dialog.querySelector('[data-hb-search-panel]');
      this.frame = dialog.querySelector('[data-hb-search-frame]');
      this.form = dialog.querySelector('[data-hb-search-form]');
      this.input = dialog.querySelector('[data-hb-search-input]');
      this.clearButton = dialog.querySelector('[data-hb-search-clear]');
      this.body = dialog.querySelector('[data-hb-search-body]');
      this.message = dialog.querySelector('[data-hb-search-message]');
      this.popular = dialog.querySelector('[data-hb-search-popular]');
      this.skeleton = dialog.querySelector('[data-hb-search-skeleton]');
      this.list = dialog.querySelector('[data-hb-search-list]');
      this.recentClear = dialog.querySelector('[data-hb-search-recent-clear]');
      this.status = dialog.querySelector('[data-hb-search-status]');
      this.strings = readJson(dialog.querySelector('[data-hb-search-strings]')) || {};
      this.cache = new Map();
      this.controller = null;
      this.timer = 0;
      this.statusTimer = 0;
      this.active = -1;
      this.state = '';
      this.query = '';
      this.closing = false;
      this.exit = [];
      this.sequence = 0;
      this.bind();
      this.renderIdle();
    }

    t(key, values = {}) { return fill(this.strings[key] ?? '', values); }

    bind() {
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', event => this.onKeydown(event));
      this.form.addEventListener('submit', event => this.onSubmit(event));
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.input.focus();
        this.onInput();
      });
      this.recentClear?.addEventListener('click', () => {
        store.clear(RECENT_KEY);
        this.morph(() => this.renderIdle());
        this.input.focus();
      });
      this.dialog.querySelector('[data-hb-search-close]')?.addEventListener('click', () => this.close());
      // The frame fills the dialog; a click on it (outside the panel) is a click on the backdrop.
      this.frame.addEventListener('click', event => { if (event.target === this.frame) this.close(); });
      this.list.addEventListener('click', event => this.onListClick(event));
      this.list.addEventListener('pointermove', event => {
        const option = event.target.closest('[role="option"]');
        if (!option) return;
        this.panel.dataset.nav = 'pointer';
        const index = this.options().indexOf(option);
        if (index !== this.active) this.setActive(index, { scroll: false });
      });
      this.list.addEventListener('pointerleave', () => { if (this.panel.dataset.nav === 'pointer') this.setActive(-1, { scroll: false }); });
      this.list.addEventListener('load', event => { if (event.target.tagName === 'IMG') event.target.classList.add('is-loaded'); }, true);
      this.list.addEventListener('error', event => { if (event.target.tagName === 'IMG') event.target.classList.add('is-loaded'); }, true);
      // Esc: animate out instead of the instant native close (keydown first; 'cancel' covers the rest).
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || event.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        this.close();
      });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => this.onClosed());
    }

    /* ---------- open / close ---------- */
    open(trigger) {
      const { dialog, input } = this;
      if (dialog.open) { input.focus(); input.select(); return; }
      this.exit.forEach(animation => animation.cancel());
      this.exit = [];
      this.ui.openDialog(dialog, trigger);
      input.focus({ preventScroll: true });
      if (input.value) input.select();
      if (!normalize(input.value)) this.renderIdle();
      this.animateIn();
    }

    animateIn() {
      if (!this.panel.animate) return;
      const reduce = reduceMotion.matches;
      const frames = reduce
        ? [{ opacity: 0 }, { opacity: 1 }]
        : wide.matches
          ? [{ opacity: 0, transform: 'translateY(-10px) scale(.965)' }, { opacity: 1, transform: 'none' }]
          : [{ opacity: 0, transform: 'translateY(18px)' }, { opacity: 1, transform: 'none' }];
      this.panel.animate(frames, { duration: reduce ? 120 : wide.matches ? 320 : 360, easing: EASE_OUT });
      if (!reduce) this.body.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 260, delay: 70, easing: 'ease-out', fill: 'backwards' });
    }

    close() {
      const { dialog, panel } = this;
      if (!dialog.open || this.closing) return;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(fallback);
        this.closing = false;
        this.ui.closeDialog(dialog);
      };
      // Animations stall in hidden tabs; never let that keep the dialog open.
      const fallback = setTimeout(finish, 320);
      if (reduceMotion.matches || !panel.animate) { finish(); return; }
      this.closing = true;
      const frames = wide.matches
        ? [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-4px) scale(.98)' }]
        : [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(12px)' }];
      const out = panel.animate(frames, { duration: 150, easing: 'cubic-bezier(.4, 0, 1, 1)', fill: 'forwards' });
      this.exit = [out];
      try { this.exit.push(dialog.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 170, easing: 'ease-in', fill: 'forwards', pseudoElement: '::backdrop' })); } catch { /* no pseudo-element animations */ }
      out.finished.then(finish, finish);
    }

    onClosed() {
      this.closing = false;
      clearTimeout(this.timer);
      this.abort();
      this.panel.classList.remove('is-busy');
      this.setActive(-1, { scroll: false });
      // Exit animations hold the closed state (fill: forwards) until the dialog is gone.
      requestAnimationFrame(() => { this.exit.forEach(animation => animation.cancel()); this.exit = []; });
    }

    /* ---------- input ---------- */
    onInput({ immediate = false } = {}) {
      const query = normalize(this.input.value);
      this.clearButton.hidden = !this.input.value;
      clearTimeout(this.timer);
      this.setActive(-1, { scroll: false });
      if (!query) {
        this.abort();
        this.query = '';
        this.morph(() => this.renderIdle());
        return;
      }
      const cached = this.cache.get(query.toLowerCase());
      if (cached) {
        this.abort();
        this.morph(() => this.renderResults(query, cached));
        return;
      }
      this.morph(() => this.renderLoading());
      if (immediate) this.run(query);
      else this.timer = setTimeout(() => this.run(query), 120);
    }

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    async run(query) {
      this.abort();
      const controller = new AbortController();
      this.controller = controller;
      let data = null;
      try {
        data = await (this.preview ? previewSearch(query) : nativeSearch(query, controller.signal));
        this.cache.set(query.toLowerCase(), data);
        if (this.cache.size > 40) this.cache.delete(this.cache.keys().next().value);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        data = null;
      }
      if (controller !== this.controller) return;
      this.controller = null;
      if (normalize(this.input.value) !== query) return;
      this.morph(() => this.renderResults(query, data));
    }

    onSubmit(event) {
      const query = normalize(this.input.value);
      const option = this.options()[this.active];
      if (option) { event.preventDefault(); option.click(); return; }
      if (!query) { event.preventDefault(); return; }
      this.remember(query);
      if (this.preview) {
        // The preview has no server-side search results page: search in place.
        event.preventDefault();
        clearTimeout(this.timer);
        if (!this.cache.has(query.toLowerCase())) this.run(query);
      }
    }

    setQuery(text) {
      this.input.value = text;
      this.input.focus();
      this.input.setSelectionRange(text.length, text.length);
      this.onInput({ immediate: true });
    }

    remember(query) {
      const term = normalize(query).slice(0, 80);
      if (term.length < 2) return;
      const list = store.read(RECENT_KEY).filter(item => typeof item === 'string' && item.toLowerCase() !== term.toLowerCase());
      store.write(RECENT_KEY, [term, ...list].slice(0, 6));
    }

    /* ---------- keyboard ---------- */
    options() { return this.list.hidden ? [] : [...this.list.querySelectorAll('[role="option"]')]; }

    onKeydown(event) {
      if (event.isComposing || event.keyCode === 229) return;
      const options = this.options();
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!options.length) return;
        event.preventDefault();
        this.panel.dataset.nav = 'keyboard';
        const next = event.key === 'ArrowDown' ? Math.min(this.active + 1, options.length - 1) : this.active - 1;
        this.setActive(Math.max(-1, next));
        return;
      }
      if (event.key === 'Enter') {
        const option = options[this.active];
        if (!option) return; // the form submits to /search
        event.preventDefault();
        if ((event.metaKey || event.ctrlKey) && option.dataset.kind !== 'query' && option.dataset.kind !== 'recent') {
          this.remember(this.query);
          window.open(option.href, '_blank', 'noopener');
          return;
        }
        option.click();
      }
    }

    setActive(index, { scroll = true } = {}) {
      const options = this.options();
      this.active = index >= 0 && index < options.length ? index : -1;
      options.forEach((option, position) => {
        const on = position === this.active;
        option.classList.toggle('is-active', on);
        option.setAttribute('aria-selected', String(on));
      });
      const current = options[this.active];
      if (current) {
        this.input.setAttribute('aria-activedescendant', current.id);
        if (scroll) {
          if (this.active === 0) this.body.scrollTo({ top: 0 });
          else current.scrollIntoView({ block: 'nearest' });
        }
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onListClick(event) {
      const option = event.target.closest('[role="option"]');
      if (!option) return;
      const kind = option.dataset.kind;
      if (kind === 'query' || kind === 'recent') {
        event.preventDefault();
        this.setQuery(option.dataset.query || '');
        return;
      }
      if (kind !== 'viewed') this.remember(this.query);
      // Links navigate natively (⌘/Ctrl-click opens a new tab); the dialog stays until the page unloads.
    }

    /* ---------- rendering ---------- */
    // Height changes of the floating panel glide instead of jumping (desktop only).
    morph(update) {
      if (!this.dialog.open || !wide.matches || reduceMotion.matches || !this.body.animate) { update(); return; }
      this.heightAnimation?.cancel();
      const from = this.body.getBoundingClientRect().height;
      update();
      const to = this.body.getBoundingClientRect().height;
      if (Math.abs(to - from) < 2) return;
      this.body.classList.add('is-morphing');
      const animation = this.body.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 240, easing: EASE_OUT });
      this.heightAnimation = animation;
      const done = () => { if (this.heightAnimation === animation) { this.body.classList.remove('is-morphing'); this.heightAnimation = null; } };
      animation.finished.then(done, done);
    }

    setState(state) {
      const previous = this.state;
      this.state = state;
      this.panel.dataset.state = state;
      return previous;
    }

    showList(html, label) {
      this.sequence = 0;
      this.list.innerHTML = html;
      this.list.hidden = !html;
      this.list.setAttribute('aria-label', label);
      this.input.setAttribute('aria-expanded', String(!!html));
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.list.querySelectorAll('img').forEach(image => { if (image.complete) image.classList.add('is-loaded'); });
    }

    announce(text) {
      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => { this.status.textContent = text; }, 450);
    }

    renderIdle() {
      this.setState('idle');
      this.panel.classList.remove('is-busy');
      this.message.hidden = true;
      this.skeleton.hidden = true;
      if (this.popular) this.popular.hidden = false;
      const here = pathOf(location.href);
      const recent = store.read(RECENT_KEY).filter(item => typeof item === 'string' && item).slice(0, LIMITS.recent);
      const viewed = store.read(VIEWED_KEY).filter(item => item && item.url && item.title && item.url !== here && !hiddenProduct(item)).slice(0, LIMITS.viewed);
      let html = '';
      if (recent.length) {
        html += this.group('recent', this.t('recent'), recent.map(term => this.option({
          kind: 'recent', href: `${root}search?type=product&q=${encodeURIComponent(term)}`, query: term,
          lead: `<span class="hb-search__glyph">${ICONS.clock}</span>`,
          main: `<span class="hb-search__title">${escape(term)}</span>`
        })));
      }
      if (viewed.length) {
        html += this.group('viewed', this.t('viewed'), viewed.map(item => this.option({
          kind: 'viewed', href: item.url,
          lead: this.media(item.image, 96),
          main: `<span class="hb-search__title">${escape(displayTitle(item.title))}</span>`,
          trail: this.finishLabel(item.finish)
        })));
      }
      if (this.recentClear) this.recentClear.hidden = !recent.length;
      this.showList(html, this.t('recent_label'));
      this.body.hidden = !html && !this.popular;
      this.status.textContent = '';
      clearTimeout(this.statusTimer);
    }

    renderLoading() {
      if (this.state === 'results' || this.state === 'none') {
        this.panel.classList.add('is-busy');
        return;
      }
      this.setState('loading');
      this.body.hidden = false;
      this.message.hidden = true;
      if (this.popular) this.popular.hidden = true;
      if (this.recentClear) this.recentClear.hidden = true;
      this.showList('', this.t('results_label'));
      this.skeleton.hidden = false;
      this.panel.classList.add('is-busy');
    }

    renderResults(query, data) {
      this.query = query;
      this.panel.classList.remove('is-busy');
      this.skeleton.hidden = true;
      this.body.hidden = false;
      if (this.recentClear) this.recentClear.hidden = true;
      const products = (data?.products || []).slice(0, LIMITS.products);
      const collections = (data?.collections || []).slice(0, LIMITS.collections);
      const pages = (data?.pages || []).slice(0, LIMITS.pages);
      const lower = query.toLowerCase();
      const queries = (data?.queries || []).filter(item => item.text && item.text.toLowerCase() !== lower).slice(0, LIMITS.queries);
      const count = products.length + collections.length + pages.length;

      if (!count && !queries.length) {
        this.setState('none');
        this.showList('', this.t('results_label'));
        this.message.hidden = false;
        this.message.innerHTML = `<p class="hb-search__message-title">${escape(this.t('no_results', { query }))}</p><p class="hb-search__message-hint">${escape(this.t('no_results_hint'))}</p>`;
        if (this.popular) this.popular.hidden = false;
        this.announce(this.t('no_results', { query }));
        return;
      }

      const previous = this.setState('results');
      this.message.hidden = true;
      if (this.popular) this.popular.hidden = true;
      let html = '';
      const [top, ...rest] = products;
      if (top) html += this.group('top', this.t('top_hit'), [this.productOption(top, query, true)]);
      if (rest.length) html += this.group('products', this.t('products'), rest.map(product => this.productOption(product, query, false)));
      if (collections.length) {
        html += this.group('collections', this.t('collections'), collections.map(collection => this.option({
          kind: 'collection', href: collection.url,
          lead: collection.image ? this.media(collection.image, 96) : `<span class="hb-search__glyph hb-search__glyph--tile">${ICONS.grid}</span>`,
          main: `<span class="hb-search__title">${highlight(collection.title, query)}</span>`,
          trail: `<span class="hb-search__kind">${escape(this.t('collection'))}</span>`
        })));
      }
      if (pages.length) {
        html += this.group('pages', this.t('pages'), pages.map(page => this.option({
          kind: 'page', href: page.url,
          lead: `<span class="hb-search__glyph hb-search__glyph--tile">${ICONS.page}</span>`,
          main: `<span class="hb-search__title">${highlight(page.title, query)}</span>`,
          trail: `<span class="hb-search__kind">${escape(this.t('page'))}</span>`
        })));
      }
      if (queries.length) {
        html += this.group('queries', this.t('queries'), queries.map(item => this.option({
          kind: 'query', href: `${root}search?type=product&q=${encodeURIComponent(item.text)}`, query: item.text,
          lead: `<span class="hb-search__glyph">${ICONS.search}</span>`,
          main: `<span class="hb-search__title">${highlight(item.text, query)}</span>`
        })));
      }
      if (!this.preview) {
        html += this.group('all', '', [this.option({
          kind: 'all', href: `${root}search?type=product&q=${encodeURIComponent(query)}`,
          lead: `<span class="hb-search__glyph">${ICONS.arrow}</span>`,
          main: `<span class="hb-search__title">${escape(this.t('see_all', { query }))}</span>`
        })]);
      }
      this.showList(html, this.t('results_label'));
      if (previous !== 'results' && !reduceMotion.matches && this.list.animate) {
        this.options().slice(0, 9).forEach((option, index) => option.animate(
          [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }],
          { duration: 260, delay: index * 22, easing: EASE_OUT, fill: 'backwards' }
        ));
      }
      this.announce(count === 1 ? this.t('results_one') : this.t('results_other').replace(/\d+/, count));
    }

    group(name, label, options) {
      const heading = label ? `<div class="hb-search__heading" aria-hidden="true">${escape(label)}</div>` : '';
      return `<div class="hb-search__group hb-search__group--${name}" role="group"${label ? ` aria-label="${escape(label)}"` : ''}>${heading}${options.join('')}</div>`;
    }

    option({ kind, href, lead = '', main = '', trail = '', query = null }) {
      const id = `hb-search-option-${this.sequence++}`;
      return `<a class="hb-search__option hb-search__option--${kind}" id="${id}" role="option" aria-selected="false" tabindex="-1" href="${escape(href)}" data-kind="${kind}"${query !== null ? ` data-query="${escape(query)}"` : ''}>${lead}<span class="hb-search__main">${main}</span>${trail ? `<span class="hb-search__trail">${trail}</span>` : ''}<span class="hb-search__go">${ICONS.enter}</span></a>`;
    }

    media(src, width) {
      const image = src ? `<img src="${escape(this.ui.imageUrl(src, width))}" alt="" width="${width}" height="${width}" loading="lazy" decoding="async">` : '';
      return `<span class="hb-search__media hb-stage">${image}</span>`;
    }

    // Finish as a mono label (the cards' "GOLD" / "SILVER" line); part of the option's accessible name.
    finishLabel(finish) {
      const label = FINISHES.includes(finish) ? this.t(`finish_${finish}`) : '';
      return label ? `<span class="hb-search__kind hb-search__finish"><span class="hb-sr">, </span>${escape(label)}</span>` : '';
    }

    productOption(product, query, top) {
      const { money } = this.ui;
      const title = displayTitle(product.title);
      const finish = this.finishLabel(product.finish);
      const sale = product.compare > product.price;
      const price = `<span class="hb-price hb-search__price${sale ? ' hb-price--sale' : ''}"><span class="hb-price__current">${escape(money(product.price))}</span>${sale ? `<s class="hb-price__compare"><span class="hb-sr">${escape(this.t('regular_price'))}</span>${escape(money(product.compare))}</s>` : ''}</span>`;
      const soldOut = product.available ? '' : `<span class="hb-search__kind">${escape(this.t('sold_out'))}</span>`;
      if (top) {
        return this.option({
          kind: 'top', href: product.url,
          lead: this.media(product.image, 200),
          main: `<span class="hb-search__title">${highlight(title, query)}</span><span class="hb-search__meta">${finish}${price}${soldOut}</span>`
        });
      }
      return this.option({
        kind: 'product', href: product.url,
        lead: this.media(product.image, 96),
        main: `<span class="hb-search__title">${highlight(title, query)}</span>`,
        trail: `${finish}${soldOut}${price}`
      });
    }
  }

  /* ---------- wiring ---------- */
  let instance = null;
  function getSearch() {
    const dialog = document.getElementById('hb-search');
    if (!dialog || !window.HumblerUI || !dialog.querySelector('[data-hb-search-input]')) return null;
    if (!instance || instance.dialog !== dialog) instance = new Search(dialog); // re-created after a theme-editor reload
    return instance;
  }
  const typing = target => target instanceof Element && !!target.closest(
    'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="range"]):not([type="color"]):not([type="file"]), textarea, select, [contenteditable]:not([contenteditable="false"])'
  );

  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-hb-search-open]');
    if (!opener || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const search = getSearch();
    if (!search) return; // the link goes to /search
    event.preventDefault();
    search.open(opener);
  });

  document.addEventListener('keydown', event => {
    if (event.defaultPrevented || event.isComposing || event.repeat) return;
    const chord = (event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey;
    const slash = event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey;
    if (!chord && !slash) return;
    const dialog = document.getElementById('hb-search');
    if (!dialog) return;
    if (dialog.open) {
      if (chord) { event.preventDefault(); getSearch()?.close(); }
      return;
    }
    // Never while the shopper is typing somewhere else (Ctrl+K also deletes to line end in macOS fields).
    if (typing(event.target)) return;
    // Another dialog (menu, bag, size guide) owns the screen.
    if (document.querySelector('dialog[open]')) return;
    const search = getSearch();
    if (!search) return;
    event.preventDefault();
    search.open();
  });

  // Back/forward cache: never come back to a page with the search still open.
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    const dialog = document.getElementById('hb-search');
    if (dialog?.open) dialog.close();
  });

  function init() {
    document.querySelectorAll('[data-hb-search-open]').forEach(opener => opener.setAttribute('aria-keyshortcuts', 'Meta+K Control+K /'));
    rememberViewed(readJson(document.querySelector('[data-hb-search-viewed]')));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.HumblerSearch = { open: trigger => getSearch()?.open(trigger), close: () => getSearch()?.close(), highlight };
})();
